import React from 'react';
import { motion } from 'framer-motion';
import { LineChartIcon, ShieldCheckIcon, ZapIcon, MessagesSquareIcon, BookOpenIcon, TrophyIcon } from 'lucide-react';
type Feature = {
  icon: React.ReactNode;
  title: string;
  description: string;
};
const features: Feature[] = [{
  icon: <LineChartIcon className="w-6 h-6" data-id="element-57" />,
  title: 'Interaktywne wykresy',
  description: 'Ćwicz na rzeczywistych danych historycznych z naszym zaawansowanym symulatorem rynku.'
}, {
  icon: <ShieldCheckIcon className="w-6 h-6" data-id="element-58" />,
  title: 'Zarządzanie ryzykiem',
  description: 'Naucz się profesjonalnego podejścia do kontroli ryzyka i ochrony kapitału.'
}, {
  icon: <ZapIcon className="w-6 h-6" data-id="element-59" />,
  title: 'Sygnały w czasie rzeczywistym',
  description: 'Otrzymuj powiadomienia o potencjalnych setupach zgodnych z Twoją strategią.'
}, {
  icon: <MessagesSquareIcon className="w-6 h-6" data-id="element-60" />,
  title: 'Społeczność traderów',
  description: 'Dołącz do aktywnej grupy, dziel się analizami i ucz się od innych.'
}, {
  icon: <BookOpenIcon className="w-6 h-6" data-id="element-61" />,
  title: 'Biblioteka wiedzy',
  description: 'Dostęp do setek godzin materiałów wideo, artykułów i case studies.'
}, {
  icon: <TrophyIcon className="w-6 h-6" data-id="element-62" />,
  title: 'Certyfikacja',
  description: 'Zdobądź certyfikat potwierdzający Twoje umiejętności analityczne.'
}];
export function FeaturesSection() {
  return <section id="features" className="py-24 bg-gray-900/50" data-id="element-63">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-64">
        {/* Header */}
        <div className="text-center mb-16" data-id="element-65">
          <motion.span initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-amber-400 text-sm font-medium tracking-wider uppercase" data-id="element-66">
            Platforma edukacyjna
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-3xl sm:text-4xl font-bold text-white mt-4 mb-4" data-id="element-67">
            Wszystko czego potrzebujesz
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-gray-400 max-w-2xl mx-auto" data-id="element-68">
            Nasza platforma łączy teorię z praktyką, dając Ci narzędzia do
            skutecznej nauki i rozwoju jako trader.
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-69">
          {features.map((feature, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="group" data-id="element-70">
              <div className="p-6 rounded-xl bg-gray-900/50 border border-gray-800 hover:border-gray-700 transition-colors duration-300" data-id="element-71">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-emerald-500/20 flex items-center justify-center text-amber-400 mb-4 group-hover:scale-110 transition-transform duration-300" data-id="element-72">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2" data-id="element-73">
                  {feature.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed" data-id="element-74">
                  {feature.description}
                </p>
              </div>
            </motion.div>)}
        </div>

        {/* Platform Preview */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="mt-20" data-id="element-75">
          <div className="relative rounded-2xl overflow-hidden border border-gray-800 bg-gray-900" data-id="element-76">
            {/* Mock Browser Header */}
            <div className="flex items-center gap-2 px-4 py-3 bg-gray-800 border-b border-gray-700" data-id="element-77">
              <div className="flex gap-1.5" data-id="element-78">
                <div className="w-3 h-3 rounded-full bg-red-500/80" data-id="element-79" />
                <div className="w-3 h-3 rounded-full bg-amber-500/80" data-id="element-80" />
                <div className="w-3 h-3 rounded-full bg-emerald-500/80" data-id="element-81" />
              </div>
              <div className="flex-1 mx-4" data-id="element-82">
                <div className="bg-gray-900 rounded-md px-3 py-1 text-sm text-gray-500 text-center" data-id="element-83">
                  app.winrealm.pl/dashboard
                </div>
              </div>
            </div>

            {/* Mock Dashboard Content */}
            <div className="p-6 grid grid-cols-12 gap-4 min-h-[400px]" data-id="element-84">
              {/* Sidebar */}
              <div className="col-span-2 space-y-3" data-id="element-85">
                {[1, 2, 3, 4, 5].map(i => <div key={i} className={`h-8 rounded-lg ${i === 1 ? 'bg-emerald-500/20' : 'bg-gray-800'}`} data-id="element-86" />)}
              </div>

              {/* Main Chart Area */}
              <div className="col-span-7 bg-gray-800/50 rounded-xl p-4" data-id="element-87">
                <div className="flex items-center justify-between mb-4" data-id="element-88">
                  <div className="h-4 w-24 bg-gray-700 rounded" data-id="element-89" />
                  <div className="flex gap-2" data-id="element-90">
                    <div className="h-6 w-16 bg-gray-700 rounded" data-id="element-91" />
                    <div className="h-6 w-16 bg-emerald-500/30 rounded" data-id="element-92" />
                  </div>
                </div>
                {/* Fake candlesticks */}
                <div className="flex items-end gap-1 h-48" data-id="element-93">
                  {Array.from({
                  length: 40
                }).map((_, i) => {
                  const height = 30 + Math.random() * 70;
                  const isGreen = Math.random() > 0.45;
                  return <div key={i} className={`flex-1 rounded-sm ${isGreen ? 'bg-emerald-500' : 'bg-red-500'}`} style={{
                    height: `${height}%`
                  }} data-id="element-94" />;
                })}
                </div>
              </div>

              {/* Right Panel */}
              <div className="col-span-3 space-y-4" data-id="element-95">
                <div className="bg-gray-800/50 rounded-xl p-4" data-id="element-96">
                  <div className="h-3 w-16 bg-gray-700 rounded mb-3" data-id="element-97" />
                  <div className="text-2xl font-mono text-emerald-400" data-id="element-98">
                    +12.4%
                  </div>
                </div>
                <div className="bg-gray-800/50 rounded-xl p-4 space-y-2" data-id="element-99">
                  {[1, 2, 3].map(i => <div key={i} className="flex items-center gap-2" data-id="element-100">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-101" />
                      <div className="h-3 flex-1 bg-gray-700 rounded" data-id="element-102" />
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}